import {parse} from 'query-string';
import {of, animationFrameScheduler, concat, defer, interval} from 'rxjs';
import {bufferCount, map, startWith, switchMap, takeWhile} from 'rxjs/operators';
import {numUtil} from '@/helper/num';
import {env} from '@/helper/env';
import {mockUtil} from '@/helper/moc';
import {calcUnrealisedPnlEv} from '@/formula';
import lang from './strategy-detail/strategy-detail.lang.js';

export const botCurrency = mockUtil.isMock() ? 'MUSDT' : 'USDT';

function formatTime(time: number, unit: string): string {
  return time > 0 ? `${time}${unit} ` : '';
}

export function countdown(duration = 0, single = false): string {
  if (duration <= 0) {
    return '-';
  }

  const timeUnits = ['d', 'h', 'm', 's'];
  const timeValues = [1000 * 60 * 60 * 24, 1000 * 60 * 60, 1000 * 60, 1000];
  const timeMap = Object.fromEntries(timeUnits.map((unit, index) => [unit, timeValues[index]]));

  const {d, h, m, s} = timeMap;
  const days = Math.floor(duration / d);
  const hours = Math.floor((duration % d) / h);
  const minutes = Math.floor((duration % h) / m);
  const seconds = Math.floor((duration % m) / s);

  if (single) {
    return days > 0
      ? formatTime(days, 'd') + formatTime(hours, 'h')
      : hours > 0
      ? formatTime(hours, 'h') + formatTime(minutes, 'm')
      : minutes > 0
      ? formatTime(minutes, 'm') + formatTime(seconds, 's')
      : formatTime(seconds, 's');
  }

  return `${days > 0 ? formatTime(days, 'd') : ''}${hours > 0 ? formatTime(hours, 'h') : ''}${minutes > 0 ? formatTime(minutes, 'm') : ''}${formatTime(seconds, 's')}`.trim();
}

export function calcUnrealisedPnl(market, item, contract) {
  if (!market || !contract) {
    return {unrealisedPnl: 0, unrealisedPnlStr: '-', markPrice: '-'};
  }

  const {contractSide, contractSize, valueFactor, valueScale, priceFactor, settleCurrency} = contract;
  const unrealisedPnl = calcUnrealisedPnlEv({
    side: item.side,
    contractSide,
    multiplier: contractSize,
    qty: item?.posSize || 0,
    entryPriceEp: item?.avgEntryPrice || 0,
    exitPriceEp: market.closeEp,
    factor: valueFactor,
    priceFactor,
  });
  const unrealisedPnlStr = unrealisedPnl !== 0 ? numUtil.floor(unrealisedPnl, 4, valueScale) : '-';
  return {unrealisedPnl, unrealisedPnlStr: unrealisedPnlStr + ' ' + settleCurrency, markPrice: market.markPrice};
}

export function displayDirection(direction, LG) {
  const map = {
    LONG: 'Long',
    SHORT: 'Short',
    NEUTRAL: 'Neutral',
  };
  return LG(lang[map[direction]]);
}

export function displaySide(side, LG) {
  const map = {
    Buy: 'Long',
    Sell: 'Short',
  };
  return LG(lang[map[side]]);
}

export function reserveValue(value, precision = 4) {
  return numUtil.floor(value, precision);
}

function duration(ms) {
  return defer(() => {
    const start = Date.now();
    return concat(
      interval(0, animationFrameScheduler).pipe(
        map(() => (Date.now() - start) / ms),
        takeWhile((n) => n <= 1)
      ),
      of(1)
    );
  });
}

export function createDisplayValue$(value$) {
  return value$.pipe(
    bufferCount(2, 1),
    switchMap((x) => duration(1000).pipe(map((y) => Math.floor(x[0] + (x[1] - x[0]) * y)))),
    map((o) => numUtil.delimit(numUtil.floor(o))),
    startWith('0')
  );
}

export const botsUtil = {
  contractGrid: 'CONTRACT_GRID',
  spotGrid: 'SPOT_GRID',
  contractMartingale: 'CONTRACT_MARTIN',
  contractSignal: 'CONTRACT_SIGNAL',
  curBotsType() {
    if (env.isServer) {
      return this.contractGrid;
    }
    return (location?.search && parse(location.search)?.type) || this.contractGrid;
  },
  isContractBots() {
    return this.curBotsType()?.toUpperCase() === this.contractGrid;
  },
  isContractMartingaleBots() {
    return this.curBotsType()?.toUpperCase() === this.contractMartingale;
  },
  isContractSignalBots() {
    return this.curBotsType()?.toUpperCase() === this.contractSignal;
  },
  isSpotBots() {
    return this.curBotsType()?.toUpperCase() === this.spotGrid;
  },
  // 现货暂时不支持策略交易, 需要特殊处理
  isMockSpotBots(spotBotsIsGray = true) {
    return (mockUtil.isMock() && this.isSpotBots()) || !spotBotsIsGray;
  },
};

export function getDay(startTime) {
  const now = Date.now();
  const timeDiff = now - startTime;
  const d = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  return d > 0 ? d : 1;
}
